import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Thermometer, Users, MapPin, Award } from "lucide-react";
import { api, euro } from "@/lib/api";
import { useLang, trFormation } from "@/lib/i18n";
import { Reveal, SectionLabel, EASE } from "@/components/Reveal";

const SESSION_MER = "/media/session-mer.jpg";

const SPANS = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-4"];
const CLIPS = ["img-clip-a", "img-clip-b", "img-clip-b", "img-clip-a"];

const EXPERIENCE = [
  { icon: Thermometer, t: "Eau chauffée à 33 °C", d: "Des bassins privés à température corporelle, pour des séances longues et profondes." },
  { icon: Users, t: "Groupe de 6 à 18 personnes", d: "Un suivi individuel pendant toute la formation, entre pairs et auprès des formatrices et formateurs." },
  { icon: MapPin, t: "Trois destinations", d: "Charente-Maritime (La Rochelle), Île de La Réunion et Maurice — l'océan pour horizon." },
  { icon: Award, t: "Certifiée Qualiopi", d: "Le cursus praticien est accessible aux financements de la formation professionnelle." },
];

function FormationCard({ f, idx, navigate, tr }) {
  const clip = CLIPS[idx % CLIPS.length];
  const tall = idx === 0 || idx === 3;
  const next = (f.sessions || []).find((s) => (s.periode || "").includes("2027")) || f.sessions?.[0];
  return (
    <Reveal delay={0.06 * idx} className={SPANS[idx % SPANS.length]}>
      <article
        data-testid={`formation-card-${f.slug}`}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
          e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
        }}
        className="spotlight-card glass-card group flex h-full cursor-pointer flex-col"
        onClick={() => navigate(`/formations/${f.slug}`)}
      >
        <div className={`relative overflow-hidden ${clip} m-2.5 mb-0 ${tall ? "h-60 md:h-72" : "h-48"}`}>
          <img
            src={f.image}
            alt={f.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_40%,rgba(3,10,22,0.55)_100%)]" />
          <span className="absolute left-4 top-4 font-mono text-[11px] tracking-[0.3em] text-foam/90">
            {tr("Module")} {f.module_code}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-serif text-2xl text-foam">{f.title}</h3>
          <p className="mt-1 text-sm text-foam/55">{f.subtitle}</p>
          {next && (
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-aqua/90">
              {tr("Prochain module")}
              <span className={`mt-1 block font-semibold ${(next.lieu || "").toLowerCase().includes("maurice") ? "text-amber-300" : "text-aqua"}`}>
                {next.periode}
              </span>
              <span className={`block ${(next.lieu || "").toLowerCase().includes("maurice") ? "text-amber-300" : "text-aqua"}`}>
                {next.lieu}
              </span>
            </p>
          )}
          {(f.sessions || []).filter((s) => s !== next && s.periode).length > 0 && (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-foam/45" data-testid={`formation-sessions-${f.slug}`}>
              {(f.sessions || []).filter((s) => s !== next && s.periode).map((s) => {
                const maurice = (s.lieu || "").toLowerCase().includes("maurice");
                return (
                  <span key={s.id} className={`mt-2 block first:mt-0 ${maurice ? "text-amber-200/90" : "text-aqua/80"}`}>
                    <span className="block">{s.periode}</span>
                    <span className="block">{s.lieu}</span>
                  </span>
                );
              })}
            </p>
          )}
          <div className="mt-auto flex items-center justify-between pt-6">
            <span className={`font-serif text-xl ${f.price > 0 ? "text-foam" : "italic text-aqua"}`} data-testid={`formation-price-${f.slug}`}>
              {f.price > 0 ? euro(f.price) : tr("Tarif sur demande")}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-aqua transition-colors group-hover:text-foam">
              {tr("Découvrir")}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Formations() {
  const [formations, setFormations] = useState([]);
  const navigate = useNavigate();
  const { tr } = useLang();

  useEffect(() => {
    api.get("/formations").then((r) => setFormations(r.data)).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-abyss pt-[72px] pb-24" data-testid="formations-page">
      <div className="mx-auto max-w-7xl px-5 pt-14 md:px-10">
        <Reveal>
          <SectionLabel>{tr("Les formations")}</SectionLabel>
          <h1 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight text-foam">
            {tr("Quatre modules & un soin spécial")}
          </h1>
          <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-foam/60">
            {tr("Un cursus progressif, de la surface à la maîtrise. Les modules durent cinq jours, le soin spécial Femmes Enceintes deux jours — en bassin privé chauffé à 33 °C, en groupe de 6 à 18 personnes.")}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 md:grid-cols-6">
          {formations.map((f, i) => (
            <FormationCard key={f.slug} f={trFormation(f, tr)} idx={i} navigate={navigate} tr={tr} />
          ))}
        </div>

        <section className="pt-24" data-testid="experience-section">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <div className="img-clip-a relative overflow-hidden">
                <motion.img
                  src={SESSION_MER}
                  alt={tr("Séance de Janzu en mer, eau turquoise")}
                  loading="lazy"
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: EASE }}
                  className="h-[420px] w-full object-cover md:h-[540px]"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(3,10,22,0.6)_100%)]" />
                <p className="absolute bottom-6 left-6 right-6 font-serif text-xl italic text-foam/90">
                  « {tr("Le Janzu invite à redécouvrir un refuge intérieur de paix et de silence.")} »
                </p>
              </div>
            </Reveal>
            <div>
              <Reveal>
                <SectionLabel>{tr("L'expérience")}</SectionLabel>
                <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight text-foam">
                  {tr("Une eau à 33 °C, un silence absolu")}
                </h2>
              </Reveal>
              <div className="mt-12 space-y-2">
                {EXPERIENCE.map((x, i) => (
                  <Reveal key={x.t} delay={0.08 * i}>
                    <div className="flex items-start gap-5 border-b border-aqua/12 py-6 first:border-t">
                      <x.icon size={19} className="mt-1 shrink-0 text-aqua" />
                      <div>
                        <h3 className="text-base font-semibold text-foam">{tr(x.t)}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-foam/60">{tr(x.d)}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-5 py-16 md:py-20" data-testid="formations-video-section">
          <Reveal>
            <h2 className="text-center font-serif text-3xl md:text-4xl text-foam">{tr("Les formations en vidéo")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-foam/55">
              {tr("Quelques images de nos séances et de nos bassins — l'expérience Janzu en un coup d'œil.")}
            </p>
            <div className="glass-card mt-8 overflow-hidden rounded-2xl p-2" data-testid="formations-video-embed">
              <iframe
                src="https://www.youtube.com/embed/nZ5zwiPXpjs?rel=0"
                title="Formations Janzu — École Française de Janzu"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                className="aspect-video w-full rounded-xl"
              />
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-16 md:py-20" data-testid="calendar-section">
          <Reveal>
            <h2 className="text-center font-serif text-3xl md:text-4xl text-foam">{tr("Le calendrier 2027")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-foam/55">
              {tr("L'ensemble des formations de l'année — février et mars à La Réunion et à l'Île Maurice, puis Charente-Maritime d'avril à octobre. Cours privé possible, à vos dates.")}
            </p>
            <div className="glass-card mt-8 overflow-hidden rounded-2xl p-2">
              <img
                src="/media/calendrier-2027.jpg"
                alt="Calendrier OJANZU 2027 — toutes les formations de l'année par module, lieu et formatrice"
                data-testid="calendar-image"
                loading="lazy"
                className="w-full rounded-xl"
              />
            </div>
          </Reveal>
        </section>
      </div>
    </main>
  );
}
